// WP-1.2/1.3/1.4 (S-10 `/tx/:id`): header (amount+type+status) + Splits table (TX-4) +
// Fee legs (FE) with net calc + AuditTrail (LG-3) + Reverse action. FX panel/links land
// with WP-1.5/1.6.
//
// WP-4.2 (AU-7/AM-3/TX-4/LG-2/LG-3): S-10's own action-menu spec is "Correct · Reverse ·
// Relink · Duplicate-of… — each opens confirm with consequence line (LG-2). Adjust never
// edits in place; it writes correction events." This phase adds Correct (amount) and Relink
// (account) as real working actions, plus a genuinely-working Split (post-hoc line) editor
// (client-side TX-4 sum-validated against the transaction's own CURRENT amount before any
// request goes out, mirroring TransactionForm.tsx's own create-time split validation).
// Correct-party has no menu entry yet (not named in this phase's scope) — add one alongside
// `useCorrectTransactionParty` if/when a future phase needs it. "Duplicate-of…" is TX-6
// dedupe, WP-4.3's own scope — left as a disabled placeholder below, not fabricated.
// Every action is gated on `!tx.reversed`, same as Reverse: a reversed transaction is never
// corrected (LG-2), matching the engine's own guard.
import { formatMoney, fromMinor, parseAmountInput } from '@pfos/engine';
import { ArrowLeft, History, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountPicker } from '../components/AccountPicker';
import { Amount } from '../components/Amount';
import { AttachmentsGallery } from '../components/AttachmentsGallery';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
} from '../components/FormKit';
import { NotesSection } from '../components/NotesSection';
import { useAccount, useAccounts } from '../lib/accounts';
import { ApiError } from '../lib/api';
import {
  useCorrectTransactionAmount,
  useRelinkTransactionAccount,
  useSplitTransactionLines,
} from '../lib/corrections';
import { useReverseTransaction, useTransaction } from '../lib/transactions';

interface SplitRow {
  amount: string; // raw AmountField text
  category: string;
}

const EMPTY_SPLIT_ROWS: SplitRow[] = [
  { amount: '', category: '' },
  { amount: '', category: '' },
];

type ActionKind = 'correct-amount' | 'relink-account' | 'split' | null;

export function TransactionDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tx, isLoading, isError } = useTransaction(id);
  const { data: account } = useAccount(tx?.accountId ?? '');
  const { data: accounts } = useAccounts();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const reverseMutation = useReverseTransaction(id);

  // WP-4.2: which action's inline form is open, and which action's confirm dialog is open.
  const [activeAction, setActiveAction] = useState<ActionKind>(null);
  const [confirmAction, setConfirmAction] = useState<ActionKind>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [amountInput, setAmountInput] = useState('');
  const correctAmountMutation = useCorrectTransactionAmount(id);

  const [relinkAccountId, setRelinkAccountId] = useState('');
  const relinkAccountMutation = useRelinkTransactionAccount(id);

  const [splitRows, setSplitRows] = useState<SplitRow[]>(EMPTY_SPLIT_ROWS);
  const splitMutation = useSplitTransactionLines(id);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (isError || tx === undefined)
    return <p className="text-sm text-muted">Transaction not found.</p>;

  function closeAction(): void {
    setActiveAction(null);
    setActionError(null);
    setAmountInput('');
    setRelinkAccountId('');
    setSplitRows(EMPTY_SPLIT_ROWS);
  }

  function openAction(kind: ActionKind): void {
    closeAction();
    setActiveAction(kind);
  }

  const amountParsed =
    amountInput.trim() === ''
      ? null
      : parseAmountInput(amountInput, { defaultCurrency: tx.currency });

  const relinkAccountName =
    (accounts ?? []).find((a) => a.id === relinkAccountId)?.name ?? relinkAccountId;

  const splitParsed = splitRows.map((r) =>
    r.amount.trim() === '' ? null : parseAmountInput(r.amount, { defaultCurrency: tx.currency }),
  );
  const splitSumMinor = splitParsed.reduce<number | null>(
    (sum, p) => (sum === null || !p?.ok ? null : sum + p.money.minorUnits),
    0,
  );
  const splitRemainderMinor = splitSumMinor === null ? null : tx.amountMinor - splitSumMinor;

  return (
    <div>
      <button
        onClick={() => {
          navigate('/tx');
        }}
        className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={16} /> Transactions
      </button>

      <div className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-6">
        <p className="text-sm capitalize text-muted">
          {tx.type} {tx.reversed && '· Reversed'}
        </p>
        <Amount
          currency={tx.currency}
          minorUnits={tx.amountMinor}
          className="text-3xl font-semibold"
        />
        <p className="mt-1 text-xs text-muted">
          {tx.at.slice(0, 10)} · {account?.name ?? tx.accountId}
          {tx.category !== undefined && ` · ${tx.category}`}
          {tx.party !== undefined && ` · ${tx.party}`}
        </p>
        {tx.note !== undefined && <p className="mt-2 text-sm text-ink">{tx.note}</p>}

        {tx.lines !== undefined && tx.lines.length > 0 && (
          <table className="mt-4 w-full text-sm">
            <caption className="mb-1 text-left text-xs uppercase tracking-wide text-muted">
              Splits
            </caption>
            <tbody>
              {tx.lines.map((line, i) => (
                <tr
                  key={`${line.category ?? ''}-${String(line.amountMinor)}-${String(i)}`}
                  className="border-t border-border"
                >
                  <td className="py-1.5 text-ink">
                    {line.category ?? line.note ?? `Line ${String(i + 1)}`}
                  </td>
                  <td className="py-1.5 text-right">
                    <Amount currency={tx.currency} minorUnits={line.amountMinor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tx.feeLegs !== undefined && tx.feeLegs.length > 0 && (
          <table className="mt-4 w-full text-sm">
            <caption className="mb-1 text-left text-xs uppercase tracking-wide text-muted">
              Fee legs
            </caption>
            <tbody>
              {tx.feeLegs.map((fee, i) => (
                <tr
                  key={`${fee.type}-${String(fee.amountMinor)}-${String(i)}`}
                  className="border-t border-border"
                >
                  <td className="py-1.5 capitalize text-ink">{fee.type.replace('_', ' ')}</td>
                  <td className="py-1.5 text-right">
                    <Amount currency={tx.currency} minorUnits={fee.amountMinor} />
                  </td>
                </tr>
              ))}
              <tr className="border-t border-border font-medium">
                <td className="py-1.5 text-ink">
                  {tx.type === 'income' ? 'Net received' : 'Total including fees'}
                </td>
                <td className="py-1.5 text-right">
                  <Amount
                    currency={tx.currency}
                    minorUnits={
                      tx.type === 'income'
                        ? tx.amountMinor - tx.feeLegs.reduce((s, f) => s + f.amountMinor, 0)
                        : tx.amountMinor + tx.feeLegs.reduce((s, f) => s + f.amountMinor, 0)
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {!tx.reversed && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2">
              <button
                className={ghostButtonInlineClass}
                onClick={() => {
                  openAction('correct-amount');
                }}
              >
                Correct amount
              </button>
              <button
                className={ghostButtonInlineClass}
                onClick={() => {
                  openAction('relink-account');
                }}
              >
                Relink account
              </button>
              <button
                className={ghostButtonInlineClass}
                onClick={() => {
                  openAction('split');
                }}
              >
                Split
              </button>
              <button
                className={ghostButtonInlineClass}
                onClick={() => {
                  setReverseError(null);
                  setConfirmOpen(true);
                }}
              >
                Reverse
              </button>
              {/* TX-6 dedupe ("I entered that twice") is WP-4.3's own scope — no working
                  Duplicate-of action yet; placeholder only, documented not fabricated. */}
              <button
                className={`${ghostButtonInlineClass} cursor-not-allowed opacity-50`}
                disabled
                title="Duplicate-of is coming in WP-4.3 (TX-6 duplicate detection)"
              >
                Duplicate of…
              </button>
            </div>
            {reverseError !== null && <p className="mt-2 text-sm text-danger">{reverseError}</p>}
            {actionError !== null && <p className="mt-2 text-sm text-danger">{actionError}</p>}

            {activeAction === 'correct-amount' && (
              <div className="mt-3 rounded-[var(--radius-control)] border border-border p-3">
                <Field label="New amount" htmlFor="correct-amount-input">
                  <AmountField
                    id="correct-amount-input"
                    currency={tx.currency}
                    value={amountInput}
                    onChange={setAmountInput}
                    autoFocus
                  />
                </Field>
                <div className="flex gap-2">
                  <button
                    className={primaryButtonInlineClass}
                    onClick={() => {
                      if (amountParsed?.ok !== true) {
                        setActionError('Enter a valid amount');
                        return;
                      }
                      setActionError(null);
                      setConfirmAction('correct-amount');
                    }}
                  >
                    Continue
                  </button>
                  <button className={ghostButtonInlineClass} onClick={closeAction}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {activeAction === 'relink-account' && (
              <div className="mt-3 rounded-[var(--radius-control)] border border-border p-3">
                <Field label="New account" htmlFor="relink-account-input">
                  {/* FX-5/LG-4: a transaction can't cross currencies (see the engine's own
                      relinkTransactionAccount guard) — filter the picker the same way
                      Owed.tsx's settle-account picker already does. */}
                  <AccountPicker
                    id="relink-account-input"
                    value={relinkAccountId}
                    onChange={setRelinkAccountId}
                    currencyFilter={tx.currency}
                  />
                </Field>
                <div className="flex gap-2">
                  <button
                    className={primaryButtonInlineClass}
                    onClick={() => {
                      if (relinkAccountId === '') {
                        setActionError('Choose an account');
                        return;
                      }
                      setActionError(null);
                      setConfirmAction('relink-account');
                    }}
                  >
                    Continue
                  </button>
                  <button className={ghostButtonInlineClass} onClick={closeAction}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {activeAction === 'split' && (
              <div className="mt-3 rounded-[var(--radius-control)] border border-border p-3">
                {splitRows.map((row, i) => (
                  <div key={`split-row-${String(i)}`} className="mb-2 flex gap-2">
                    <div className="flex-1">
                      <AmountField
                        id={`split-row-amount-${String(i)}`}
                        ariaLabel={`Line ${String(i + 1)} amount`}
                        currency={tx.currency}
                        value={row.amount}
                        onChange={(v) => {
                          setSplitRows((prev) =>
                            prev.map((r, j) => (j === i ? { ...r, amount: v } : r)),
                          );
                        }}
                      />
                    </div>
                    <input
                      className={`${inputClass} flex-1`}
                      placeholder="Category"
                      aria-label={`Line ${String(i + 1)} category`}
                      value={row.category}
                      onChange={(e) => {
                        const { value } = e.target;
                        setSplitRows((prev) =>
                          prev.map((r, j) => (j === i ? { ...r, category: value } : r)),
                        );
                      }}
                    />
                    {splitRows.length > 2 && (
                      <button
                        type="button"
                        aria-label={`Remove line ${String(i + 1)}`}
                        className="text-muted hover:text-danger"
                        onClick={() => {
                          setSplitRows((prev) => prev.filter((_, j) => j !== i));
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="mb-2 flex items-center gap-1 text-sm text-primary hover:underline"
                  onClick={() => {
                    setSplitRows((prev) => [...prev, { amount: '', category: '' }]);
                  }}
                >
                  <Plus size={14} /> Add line
                </button>
                <p
                  className={`mb-2 text-sm ${splitRemainderMinor === 0 ? 'text-muted' : 'text-danger'}`}
                >
                  {splitRemainderMinor === null
                    ? 'Enter every line'
                    : splitRemainderMinor === 0
                      ? 'Lines add up to the current amount'
                      : `Remaining: ${formatMoney(fromMinor(tx.currency, splitRemainderMinor))}`}
                </p>
                <div className="flex gap-2">
                  <button
                    className={primaryButtonInlineClass}
                    onClick={() => {
                      const bad = splitParsed.find((p) => !p?.ok);
                      if (bad !== undefined || splitParsed.some((p) => p === null)) {
                        setActionError('Enter a valid amount for every line');
                        return;
                      }
                      if (splitRemainderMinor !== 0) {
                        setActionError('Split lines must add up to the current amount (TX-4)');
                        return;
                      }
                      setActionError(null);
                      setConfirmAction('split');
                    }}
                  >
                    Continue
                  </button>
                  <button className={ghostButtonInlineClass} onClick={closeAction}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <AttachmentsGallery entityType="transaction" entityId={id} />
        <NotesSection entityType="transaction" entityId={id} />
      </div>

      <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-ink">
          <History size={16} /> History
        </p>
        <ul className="space-y-2">
          {tx.history.map((h) => (
            <li key={`${h.kind}-${h.at}`} className="text-sm text-muted">
              <span className="capitalize text-ink">{h.kind}</span> ·{' '}
              {h.at.slice(0, 16).replace('T', ' ')}
              {h.reason !== undefined && <span> — {h.reason}</span>}
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Reverse this transaction?"
        consequence="This creates a reversal event; the original stays in your history (LG-2)."
        confirmLabel="Reverse"
        requireReason
        reasonLabel="Reason"
        confirming={reverseMutation.isPending}
        onConfirm={(reason) => {
          reverseMutation.mutate(reason, {
            onSuccess: () => {
              setConfirmOpen(false);
            },
            onError: (e) => {
              setReverseError(e instanceof ApiError ? e.message : 'Could not reverse');
              setConfirmOpen(false);
            },
          });
        }}
      />

      <ConfirmDialog
        open={confirmAction === 'correct-amount'}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title="Correct the amount?"
        consequence={`This will change the recorded amount from ${formatMoney(fromMinor(tx.currency, tx.amountMinor))} to ${
          amountParsed?.ok === true ? formatMoney(amountParsed.money) : ''
        }. History is kept.`}
        confirmLabel="Correct"
        confirming={correctAmountMutation.isPending}
        onConfirm={() => {
          if (amountParsed?.ok !== true) return;
          correctAmountMutation.mutate(amountParsed.money.minorUnits, {
            onSuccess: () => {
              setConfirmAction(null);
              closeAction();
            },
            onError: (e) => {
              setActionError(e instanceof ApiError ? e.message : 'Could not correct the amount');
              setConfirmAction(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={confirmAction === 'relink-account'}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title="Relink the account?"
        consequence={`This will move this transaction from ${account?.name ?? tx.accountId} to ${relinkAccountName}. History is kept.`}
        confirmLabel="Relink"
        confirming={relinkAccountMutation.isPending}
        onConfirm={() => {
          if (relinkAccountId === '') return;
          relinkAccountMutation.mutate(relinkAccountId, {
            onSuccess: () => {
              setConfirmAction(null);
              closeAction();
            },
            onError: (e) => {
              setActionError(e instanceof ApiError ? e.message : 'Could not relink the account');
              setConfirmAction(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={confirmAction === 'split'}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title="Split into lines?"
        consequence={`This replaces the current lines with ${String(splitRows.length)} new lines totaling ${formatMoney(
          fromMinor(tx.currency, tx.amountMinor),
        )}. History is kept.`}
        confirmLabel="Split"
        confirming={splitMutation.isPending}
        onConfirm={() => {
          if (splitRemainderMinor !== 0 || splitParsed.some((p) => !p?.ok)) return;
          const lines = splitRows.map((row, i) => {
            const parsed = splitParsed[i];
            return {
              amountMinor: parsed?.ok === true ? parsed.money.minorUnits : 0,
              ...(row.category !== '' ? { category: row.category } : {}),
            };
          });
          splitMutation.mutate(lines, {
            onSuccess: () => {
              setConfirmAction(null);
              closeAction();
            },
            onError: (e) => {
              setActionError(e instanceof ApiError ? e.message : 'Could not split the transaction');
              setConfirmAction(null);
            },
          });
        }}
      />
    </div>
  );
}
